// ============================================
// CART LIMIT GUARD (standalone - works with any cart JS)
// Limits: window.CART_LIMITS = { min: 1, max: 5 }
// ============================================
(() => {
  'use strict';

  if (window.__CART_LIMIT_GUARD__) return;
  window.__CART_LIMIT_GUARD__ = true;

  const CART_SELECTOR = '#CartDrawer, cart-drawer, #cart-drawer, .cart-drawer';
  const GENERIC = /error while updating your cart/i;

  const limits = () => Object.assign({ min: 1, max: 5 }, window.CART_LIMITS || {});
  const maxText = () => `Maximum quantity of ${limits().max} per product is already added to your cart.`;
  const minText = () => `Minimum quantity of ${limits().min} is required for this product.`;
  const isGeneric = (text) =>
    GENERIC.test(text || '') ||
    (!!window.cartStrings?.error && String(text || '').includes(window.cartStrings.error));

  // --- INJECT CSS TO FIX LAYOUT ---
  // This forces the quantity box to stay visible and the message to drop below it
  const style = document.createElement('style');
  style.textContent = `
    .cart-item__quantity-wrapper { flex-wrap: wrap !important; }
    .cart-item__quantity { display: block !important; }
    .quantity { flex-shrink: 0 !important; } 
    .dvik-limit-note {
      color: #c62828;
      font-size: 1.2rem;
      line-height: 1.4;
      margin-top: 8px;
      width: 100%;
      flex-basis: 100%;
      display: block;
      text-align: left;
      clear: both;
    }
  `;
  document.head.appendChild(style);

  // ---------- inline message under the quantity box ----------
  function showMessage(input, text) {
    const wrapper = input.closest('quantity-input, .quantity');
    if (!wrapper) return;

    // Find the container that holds BOTH the quantity and the trash/remove button
    const itemWrapper = wrapper.closest('.cart-item__quantity-wrapper') || wrapper.parentElement;
    
    let note = itemWrapper.querySelector('.dvik-limit-note');
    if (!note) {
      note = document.createElement('div');
      note.className = 'dvik-limit-note';
      note.setAttribute('role', 'alert');
      
      // Append to the main wrapper so it drops to a new line
      itemWrapper.appendChild(note);
    }
    note.textContent = text;
    
    clearTimeout(note._hideTimer);
    note._hideTimer = setTimeout(() => note.remove(), 4000);
  }

  function block(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  // ---------- 1a) + / - buttons ----------
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('.quantity__button');
    if (!button || !button.closest(CART_SELECTOR)) return;

    const holder = button.closest('quantity-input, .quantity');
    const input = holder?.querySelector('input');
    if (!input) return;

    const value = parseInt(input.value);
    if (isNaN(value)) return;

    const { min, max } = limits();
    const all = [...holder.querySelectorAll('.quantity__button')];
    const isPlus = button.name ? button.name === 'plus' : button === all[all.length - 1];

    if (isPlus && value >= max) {
      block(event);
      showMessage(input, maxText());
    } else if (!isPlus && value - 1 > 0 && value - 1 < min) {
      block(event);
      showMessage(input, minText());
    }
  }, true);

  // ---------- 1b) typed quantity ----------
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.closest(CART_SELECTOR)) return;
    if (input.type !== 'number' && !input.classList.contains('quantity__input')) return;

    const value = parseInt(input.value);
    if (isNaN(value)) return;
    const { min, max } = limits();

    if (value > max) {
      event.stopImmediatePropagation();
      input.value = input.getAttribute('value') || String(max);
      showMessage(input, maxText());
    } else if (value !== 0 && value < min) {
      event.stopImmediatePropagation();
      input.value = input.getAttribute('value') || String(min);
      showMessage(input, minText());
    }
  }, true);

  // ---------- 2) Fix the theme's generic error overriding the input box ----------
  let busy = false;

  async function fixGenericError() {
    if (busy) return;
    busy = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;
      const cart = await response.json();
      const { max } = limits();

      const atLimit = (cart.items || []).some(
        (item) => item.quantity >= max && item.properties?._gift !== 'true'
      );
      if (!atLimit) return; 

      // Remove the theme's broken generic error text
      document.querySelectorAll(CART_SELECTOR).forEach((rootEl) => {
        const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT);
        let node;
        while ((node = walker.nextNode())) {
          if (isGeneric(node.nodeValue)) node.nodeValue = ''; 
        }
      });
      
      document.querySelectorAll('#cart-errors, #CartDrawer-CartErrors').forEach((el) => {
        if (isGeneric(el.textContent)) el.textContent = '';
      });

      // FORCE the quantity boxes to reappear and show the correct number
      (cart.items || []).forEach((item, index) => {
        const line = index + 1;
        const row =
          document.getElementById(`CartDrawer-Item-${line}`) ||
          document.getElementById(`CartItem-${line}`);
        
        const input = row?.querySelector('input.quantity__input, input[type="number"]');
        if (input) {
          input.value = String(item.quantity);
          input.setAttribute('value', String(item.quantity));
          input.style.display = ''; // Force the input to be visible
        }
        
        // Make sure the quantity container itself is not hidden
        const qtyContainer = row?.querySelector('.cart-item__quantity');
        if (qtyContainer) qtyContainer.style.display = '';
      });

      // Re-show the limit message now that the input is visible
      const firstInput = document.querySelector(`${CART_SELECTOR} input.quantity__input`);
      if (firstInput) showMessage(firstInput, maxText());

    } catch (e) {
      console.error('[cart-limit-guard]', e);
    } finally {
      busy = false;
    }
  }

  function containsGeneric(node) {
    if (node.nodeType === 3) return isGeneric(node.nodeValue);
    if (node.nodeType === 1) return isGeneric(node.textContent);
    return false;
  }

  function startObserver() {
    const cartRoot = document.querySelector(CART_SELECTOR);
    if (!cartRoot) return;

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'characterData' && isGeneric(m.target.nodeValue)) {
          if (m.target.parentElement?.closest(CART_SELECTOR)) return void fixGenericError();
        }
        for (const n of m.addedNodes) {
          const el = n.nodeType === 1 ? n : n.parentElement;
          if (el && (el.closest?.(CART_SELECTOR) || el.querySelector?.(CART_SELECTOR)) && containsGeneric(n)) {
            return void fixGenericError();
          }
        }
      }
    });
    observer.observe(cartRoot, { childList: true, subtree: true, characterData: true });
  }

  if (document.body) startObserver();
  else document.addEventListener('DOMContentLoaded', startObserver, { once: true });
})();